// SPDX-License-Identifier: AGPL-3.0-or-later

import {assign, getInitialSnapshot, type SnapshotFrom, setup, transition} from 'xstate';

export type VideoQualityLevel = 'low' | 'medium' | 'high';
export type VoiceSubscriptionContext = 'focused' | 'carousel' | 'hidden';
export type VoiceSubscriptionObservedElement = object | null;

export interface VoiceRemoteTrackSubscriptionController {
	setRemoteTrackSubscription(options: {
		participantIdentity: string;
		source: string;
		subscribed: boolean;
		enabled?: boolean;
		quality?: VideoQualityLevel;
	}): void;
}

export interface VoiceSubscriptionEntry {
	subscribed: boolean;
	publicationAvailable: boolean;
	enabled: boolean;
	quality: VideoQualityLevel;
	context: VoiceSubscriptionContext;
	isIntersecting: boolean;
	observedElement: VoiceSubscriptionObservedElement;
}

export type VoiceSubscriptionCommand =
	| {
			type: 'subscribePublication';
			participantIdentity: string;
			enabled: boolean;
			quality: VideoQualityLevel;
	  }
	| {type: 'unsubscribePublication'; participantIdentity: string}
	| {type: 'setPublicationEnabled'; participantIdentity: string; enabled: boolean}
	| {type: 'setPublicationQuality'; participantIdentity: string; quality: VideoQualityLevel}
	| {
			type: 'observeElement';
			participantIdentity: string;
			element: NonNullable<VoiceSubscriptionObservedElement>;
	  }
	| {type: 'disconnectObserver'; participantIdentity: string};

interface VoiceSubscriptionMachineContext {
	entries: ReadonlyMap<string, VoiceSubscriptionEntry>;
	commands: ReadonlyArray<VoiceSubscriptionCommand>;
}

export type VoiceSubscriptionEvent =
	| {
			type: 'subscription.subscribe';
			participantIdentity: string;
			hasPublication: boolean;
			observedElement: VoiceSubscriptionObservedElement;
			quality?: VideoQualityLevel;
			context?: VoiceSubscriptionContext;
	  }
	| {type: 'subscription.unsubscribe'; participantIdentity: string}
	| {
			type: 'subscription.replaceObserver';
			participantIdentity: string;
			hasPublication: boolean;
			observedElement: VoiceSubscriptionObservedElement;
	  }
	| {
			type: 'subscription.intersection';
			participantIdentity: string;
			hasPublication: boolean;
			isIntersecting: boolean;
	  }
	| {
			type: 'subscription.setEnabled';
			participantIdentity: string;
			hasPublication: boolean;
			enabled: boolean;
	  }
	| {
			type: 'subscription.setQuality';
			participantIdentity: string;
			hasPublication: boolean;
			quality: VideoQualityLevel;
	  }
	| {
			type: 'subscription.setContext';
			participantIdentity: string;
			hasPublication: boolean;
			context: VoiceSubscriptionContext;
	  }
	| {type: 'subscription.reattachAfterPublish'; participantIdentity: string; hasPublication: boolean}
	| {type: 'subscription.publicationMissing'; participantIdentity: string}
	| {type: 'subscription.cleanup'}
	| {type: 'subscription.clearCommands'};

const EMPTY_COMMANDS: ReadonlyArray<VoiceSubscriptionCommand> = [];

function initialContext(
	commands: ReadonlyArray<VoiceSubscriptionCommand> = EMPTY_COMMANDS,
): VoiceSubscriptionMachineContext {
	return {
		entries: new Map(),
		commands,
	};
}

export function getVoiceSubscriptionQualityForContext(context: VoiceSubscriptionContext): VideoQualityLevel {
	switch (context) {
		case 'focused':
			return 'high';
		case 'carousel':
			return 'medium';
		case 'hidden':
			return 'low';
	}
}

export function shouldEnableVoiceSubscription(
	context: VoiceSubscriptionContext,
	isIntersecting: boolean,
	observedElement: VoiceSubscriptionObservedElement,
): boolean {
	if (context === 'focused') return true;
	if (context === 'hidden') return false;
	return observedElement == null || isIntersecting;
}

function appendCommands(
	context: VoiceSubscriptionMachineContext,
	commands: ReadonlyArray<VoiceSubscriptionCommand>,
): VoiceSubscriptionMachineContext {
	if (commands.length === 0) return context;
	return {
		...context,
		commands: [...context.commands, ...commands],
	};
}

function setEntry(
	context: VoiceSubscriptionMachineContext,
	participantIdentity: string,
	entry: VoiceSubscriptionEntry,
	commands: ReadonlyArray<VoiceSubscriptionCommand> = EMPTY_COMMANDS,
): VoiceSubscriptionMachineContext {
	const entries = new Map(context.entries);
	entries.set(participantIdentity, entry);
	return appendCommands({...context, entries}, commands);
}

function deleteEntry(
	context: VoiceSubscriptionMachineContext,
	participantIdentity: string,
	commands: ReadonlyArray<VoiceSubscriptionCommand>,
): VoiceSubscriptionMachineContext {
	if (!context.entries.has(participantIdentity)) return context;
	const entries = new Map(context.entries);
	entries.delete(participantIdentity);
	return appendCommands({...context, entries}, commands);
}

function observerReplacementCommands(
	participantIdentity: string,
	previousElement: VoiceSubscriptionObservedElement,
	nextElement: VoiceSubscriptionObservedElement,
): Array<VoiceSubscriptionCommand> {
	if (previousElement === nextElement) return [];
	const commands: Array<VoiceSubscriptionCommand> = [];
	if (previousElement) commands.push({type: 'disconnectObserver', participantIdentity});
	if (nextElement) commands.push({type: 'observeElement', participantIdentity, element: nextElement});
	return commands;
}

function subscribePublicationCommand(
	participantIdentity: string,
	entry: Pick<VoiceSubscriptionEntry, 'enabled' | 'quality'>,
): VoiceSubscriptionCommand {
	return {
		type: 'subscribePublication',
		participantIdentity,
		enabled: entry.enabled,
		quality: entry.quality,
	};
}

function updatePublicationAvailability(entry: VoiceSubscriptionEntry, hasPublication: boolean): VoiceSubscriptionEntry {
	if (entry.publicationAvailable === hasPublication) return entry;
	return {...entry, publicationAvailable: hasPublication};
}

function subscribe(
	context: VoiceSubscriptionMachineContext,
	event: Extract<VoiceSubscriptionEvent, {type: 'subscription.subscribe'}>,
): VoiceSubscriptionMachineContext {
	const existing = context.entries.get(event.participantIdentity);
	const nextContext = event.context ?? existing?.context ?? 'carousel';
	const nextQuality =
		event.quality ?? (event.context ? getVoiceSubscriptionQualityForContext(nextContext) : existing?.quality) ?? 'low';
	const elementChanged = existing ? existing.observedElement !== event.observedElement : event.observedElement != null;
	const isIntersecting = elementChanged ? false : (existing?.isIntersecting ?? false);
	const enabled = shouldEnableVoiceSubscription(nextContext, isIntersecting, event.observedElement);
	const nextEntry: VoiceSubscriptionEntry = {
		subscribed: true,
		publicationAvailable: event.hasPublication,
		enabled,
		quality: nextQuality,
		context: nextContext,
		isIntersecting,
		observedElement: event.observedElement,
	};
	const commands = existing
		? observerReplacementCommands(event.participantIdentity, existing.observedElement, event.observedElement)
		: observerReplacementCommands(event.participantIdentity, null, event.observedElement);

	if (event.hasPublication) {
		if (!existing || !existing.publicationAvailable) {
			commands.push(subscribePublicationCommand(event.participantIdentity, nextEntry));
		} else {
			if (existing.enabled !== enabled) {
				commands.push({type: 'setPublicationEnabled', participantIdentity: event.participantIdentity, enabled});
			}
			if (existing.quality !== nextQuality) {
				commands.push({
					type: 'setPublicationQuality',
					participantIdentity: event.participantIdentity,
					quality: nextQuality,
				});
			}
		}
	}

	return setEntry(context, event.participantIdentity, nextEntry, commands);
}

function unsubscribe(
	context: VoiceSubscriptionMachineContext,
	participantIdentity: string,
): VoiceSubscriptionMachineContext {
	const entry = context.entries.get(participantIdentity);
	if (!entry) return context;
	const commands = observerReplacementCommands(participantIdentity, entry.observedElement, null);
	commands.push({type: 'unsubscribePublication', participantIdentity});
	return deleteEntry(context, participantIdentity, commands);
}

function replaceObserver(
	context: VoiceSubscriptionMachineContext,
	event: Extract<VoiceSubscriptionEvent, {type: 'subscription.replaceObserver'}>,
): VoiceSubscriptionMachineContext {
	const entry = context.entries.get(event.participantIdentity);
	if (!entry || entry.observedElement === event.observedElement) return context;
	const nextEntry = updatePublicationAvailability(
		{
			...entry,
			observedElement: event.observedElement,
			isIntersecting: false,
			enabled: shouldEnableVoiceSubscription(entry.context, false, event.observedElement),
		},
		event.hasPublication,
	);
	const commands = observerReplacementCommands(event.participantIdentity, entry.observedElement, event.observedElement);
	if (event.hasPublication) {
		if (!entry.publicationAvailable) {
			commands.push(subscribePublicationCommand(event.participantIdentity, nextEntry));
		} else if (entry.enabled !== nextEntry.enabled) {
			commands.push({
				type: 'setPublicationEnabled',
				participantIdentity: event.participantIdentity,
				enabled: nextEntry.enabled,
			});
		}
	}
	return setEntry(context, event.participantIdentity, nextEntry, commands);
}

function intersectionChanged(
	context: VoiceSubscriptionMachineContext,
	event: Extract<VoiceSubscriptionEvent, {type: 'subscription.intersection'}>,
): VoiceSubscriptionMachineContext {
	const entry = context.entries.get(event.participantIdentity);
	if (!entry) return context;
	const enabled = shouldEnableVoiceSubscription(entry.context, event.isIntersecting, entry.observedElement);
	const nextEntry = updatePublicationAvailability(
		{
			...entry,
			isIntersecting: event.isIntersecting,
			enabled,
		},
		event.hasPublication,
	);
	const commands: Array<VoiceSubscriptionCommand> = [];
	if (event.hasPublication) {
		if (!entry.publicationAvailable) {
			commands.push(subscribePublicationCommand(event.participantIdentity, nextEntry));
		} else if (entry.enabled !== enabled) {
			commands.push({type: 'setPublicationEnabled', participantIdentity: event.participantIdentity, enabled});
		}
	}
	return setEntry(context, event.participantIdentity, nextEntry, commands);
}

function setEnabled(
	context: VoiceSubscriptionMachineContext,
	event: Extract<VoiceSubscriptionEvent, {type: 'subscription.setEnabled'}>,
): VoiceSubscriptionMachineContext {
	const entry = context.entries.get(event.participantIdentity);
	if (!entry) return context;
	const nextEntry = updatePublicationAvailability({...entry, enabled: event.enabled}, event.hasPublication);
	const commands: Array<VoiceSubscriptionCommand> = [];
	if (event.hasPublication) {
		if (!entry.publicationAvailable) {
			commands.push(subscribePublicationCommand(event.participantIdentity, nextEntry));
		} else if (entry.enabled !== event.enabled) {
			commands.push({
				type: 'setPublicationEnabled',
				participantIdentity: event.participantIdentity,
				enabled: event.enabled,
			});
		}
	}
	return setEntry(context, event.participantIdentity, nextEntry, commands);
}

function setQuality(
	context: VoiceSubscriptionMachineContext,
	event: Extract<VoiceSubscriptionEvent, {type: 'subscription.setQuality'}>,
): VoiceSubscriptionMachineContext {
	const entry = context.entries.get(event.participantIdentity);
	if (!entry) return context;
	const nextEntry = updatePublicationAvailability({...entry, quality: event.quality}, event.hasPublication);
	const commands: Array<VoiceSubscriptionCommand> = [];
	if (event.hasPublication) {
		if (!entry.publicationAvailable) {
			commands.push(subscribePublicationCommand(event.participantIdentity, nextEntry));
		} else if (entry.quality !== event.quality) {
			commands.push({
				type: 'setPublicationQuality',
				participantIdentity: event.participantIdentity,
				quality: event.quality,
			});
		}
	}
	return setEntry(context, event.participantIdentity, nextEntry, commands);
}

function setContext(
	context: VoiceSubscriptionMachineContext,
	event: Extract<VoiceSubscriptionEvent, {type: 'subscription.setContext'}>,
): VoiceSubscriptionMachineContext {
	const entry = context.entries.get(event.participantIdentity);
	if (!entry) return context;
	const quality = getVoiceSubscriptionQualityForContext(event.context);
	const enabled = shouldEnableVoiceSubscription(event.context, entry.isIntersecting, entry.observedElement);
	const nextEntry = updatePublicationAvailability(
		{
			...entry,
			context: event.context,
			enabled,
			quality,
		},
		event.hasPublication,
	);
	const commands: Array<VoiceSubscriptionCommand> = [];
	if (event.hasPublication) {
		if (!entry.publicationAvailable) {
			commands.push(subscribePublicationCommand(event.participantIdentity, nextEntry));
		} else {
			if (entry.enabled !== enabled) {
				commands.push({type: 'setPublicationEnabled', participantIdentity: event.participantIdentity, enabled});
			}
			if (entry.quality !== quality) {
				commands.push({
					type: 'setPublicationQuality',
					participantIdentity: event.participantIdentity,
					quality,
				});
			}
		}
	}
	return setEntry(context, event.participantIdentity, nextEntry, commands);
}

function reattachAfterPublish(
	context: VoiceSubscriptionMachineContext,
	event: Extract<VoiceSubscriptionEvent, {type: 'subscription.reattachAfterPublish'}>,
): VoiceSubscriptionMachineContext {
	const entry = context.entries.get(event.participantIdentity);
	if (!entry) return context;
	const nextEntry = updatePublicationAvailability(entry, event.hasPublication);
	const commands = event.hasPublication
		? [subscribePublicationCommand(event.participantIdentity, nextEntry)]
		: EMPTY_COMMANDS;
	return setEntry(context, event.participantIdentity, nextEntry, commands);
}

function publicationMissing(
	context: VoiceSubscriptionMachineContext,
	participantIdentity: string,
): VoiceSubscriptionMachineContext {
	const entry = context.entries.get(participantIdentity);
	if (!entry || !entry.publicationAvailable) return context;
	return setEntry(context, participantIdentity, {...entry, publicationAvailable: false});
}

function cleanup(context: VoiceSubscriptionMachineContext): VoiceSubscriptionMachineContext {
	if (context.entries.size === 0) return context;
	const commands: Array<VoiceSubscriptionCommand> = [];
	for (const [participantIdentity, entry] of context.entries) {
		commands.push(...observerReplacementCommands(participantIdentity, entry.observedElement, null));
		commands.push({type: 'unsubscribePublication', participantIdentity});
	}
	return appendCommands({...context, entries: new Map()}, commands);
}

export const voiceSubscriptionStateMachine = setup({
	types: {} as {
		context: VoiceSubscriptionMachineContext;
		events: VoiceSubscriptionEvent;
	},
	actions: {
		subscribe: assign(({context, event}) =>
			event.type === 'subscription.subscribe' ? subscribe(context, event) : context,
		),
		unsubscribe: assign(({context, event}) =>
			event.type === 'subscription.unsubscribe' ? unsubscribe(context, event.participantIdentity) : context,
		),
		replaceObserver: assign(({context, event}) =>
			event.type === 'subscription.replaceObserver' ? replaceObserver(context, event) : context,
		),
		intersectionChanged: assign(({context, event}) =>
			event.type === 'subscription.intersection' ? intersectionChanged(context, event) : context,
		),
		setEnabled: assign(({context, event}) =>
			event.type === 'subscription.setEnabled' ? setEnabled(context, event) : context,
		),
		setQuality: assign(({context, event}) =>
			event.type === 'subscription.setQuality' ? setQuality(context, event) : context,
		),
		setContext: assign(({context, event}) =>
			event.type === 'subscription.setContext' ? setContext(context, event) : context,
		),
		reattachAfterPublish: assign(({context, event}) =>
			event.type === 'subscription.reattachAfterPublish' ? reattachAfterPublish(context, event) : context,
		),
		publicationMissing: assign(({context, event}) =>
			event.type === 'subscription.publicationMissing'
				? publicationMissing(context, event.participantIdentity)
				: context,
		),
		cleanup: assign(({context}) => cleanup(context)),
		clearCommands: assign(({context}) =>
			context.commands.length === 0 ? context : {...context, commands: EMPTY_COMMANDS},
		),
	},
	guards: {
		hasSubscriptions: ({context}) => context.entries.size > 0,
	},
}).createMachine({
	id: 'voiceSubscription',
	context: () => initialContext(),
	initial: 'routing',
	states: {
		routing: {
			always: [{guard: 'hasSubscriptions', target: 'subscribed'}, {target: 'unsubscribed'}],
		},
		unsubscribed: {
			on: {
				'subscription.subscribe': {target: 'routing', actions: 'subscribe'},
				'subscription.unsubscribe': {target: 'routing', actions: 'unsubscribe'},
				'subscription.replaceObserver': {target: 'routing', actions: 'replaceObserver'},
				'subscription.intersection': {target: 'routing', actions: 'intersectionChanged'},
				'subscription.setEnabled': {target: 'routing', actions: 'setEnabled'},
				'subscription.setQuality': {target: 'routing', actions: 'setQuality'},
				'subscription.setContext': {target: 'routing', actions: 'setContext'},
				'subscription.reattachAfterPublish': {target: 'routing', actions: 'reattachAfterPublish'},
				'subscription.publicationMissing': {target: 'routing', actions: 'publicationMissing'},
				'subscription.cleanup': {target: 'routing', actions: 'cleanup'},
				'subscription.clearCommands': {target: 'routing', actions: 'clearCommands'},
			},
		},
		subscribed: {
			on: {
				'subscription.subscribe': {target: 'routing', actions: 'subscribe'},
				'subscription.unsubscribe': {target: 'routing', actions: 'unsubscribe'},
				'subscription.replaceObserver': {target: 'routing', actions: 'replaceObserver'},
				'subscription.intersection': {target: 'routing', actions: 'intersectionChanged'},
				'subscription.setEnabled': {target: 'routing', actions: 'setEnabled'},
				'subscription.setQuality': {target: 'routing', actions: 'setQuality'},
				'subscription.setContext': {target: 'routing', actions: 'setContext'},
				'subscription.reattachAfterPublish': {target: 'routing', actions: 'reattachAfterPublish'},
				'subscription.publicationMissing': {target: 'routing', actions: 'publicationMissing'},
				'subscription.cleanup': {target: 'routing', actions: 'cleanup'},
				'subscription.clearCommands': {target: 'routing', actions: 'clearCommands'},
			},
		},
	},
});

export type VoiceSubscriptionSnapshot = SnapshotFrom<typeof voiceSubscriptionStateMachine>;
export type VoiceSubscriptionStateValue = 'subscribed' | 'unsubscribed';

export function createVoiceSubscriptionSnapshot(): VoiceSubscriptionSnapshot {
	return getInitialSnapshot(voiceSubscriptionStateMachine);
}

export function transitionVoiceSubscriptionSnapshot(
	snapshot: VoiceSubscriptionSnapshot,
	event: VoiceSubscriptionEvent,
): VoiceSubscriptionSnapshot {
	return transition(voiceSubscriptionStateMachine, snapshot, event)[0] as VoiceSubscriptionSnapshot;
}

export function getVoiceSubscriptionStateValue(snapshot: VoiceSubscriptionSnapshot): VoiceSubscriptionStateValue {
	return snapshot.context.entries.size > 0 ? 'subscribed' : 'unsubscribed';
}
